// YERİ: src/app/domains/site/sections/sector.sections.ts
//
// Sektöre özgü bölümler: Menü, Masa Rezervasyonu, Oda Izgarası, İlan Arama,
// Ders Programı, Üyelik Planları, Personel Kartları, Randevu Kutusu.
//
// ── NEDEN SONRADAN YAZILDI ──────────────────────────────────────────────────
// Bu sekiz tip bölüm ekleme listesinde ZATEN görünüyordu ama section-host'ta
// karşılığı yoktu: müşteri "Menü" bölümünü ekliyor, kaydediyor, sitede hiçbir
// şey çıkmıyordu — hata da yok, boş bir alan. Sektörel şablonların çoğu tam
// bu bölümlere dayandığı için (restoran menüsü, otel odası, emlak araması)
// önce bunlar yazıldı.
//
// Hepsi config'ten okur ve boş bırakılırsa örnek satırlarla çizer: şablon
// uygulandığı anda sayfa dolu görünsün, müşteri kendi içeriğini üstüne yazsın.

import { Component, computed, inject, input } from '@angular/core';
import { SiteChromeText } from '../core/site-chrome-text';
import { MIcon } from '@/app/shared/ui/m-icon';
import { SiteConfig, cfg } from './section.model';

// ── Menü (restoran / kafe) ──────────────────────────────────────────────────
@Component({
  selector: 'sec-menu-display',
  standalone: true,
  template: `
    <section id="menu" class="bg-white py-24 dark:bg-neutral-950">
      <div class="mx-auto max-w-5xl px-6 lg:px-8">
        <div class="text-center">
          @if (eyebrow()) { <p class="text-sm font-semibold uppercase tracking-widest" style="color:var(--site-accent)">{{ eyebrow() }}</p> }
          <h2 class="mt-3 font-[family-name:var(--site-head-font)] text-3xl font-bold tracking-tight text-neutral-900 dark:text-white sm:text-4xl">{{ title() }}</h2>
          @if (subtitle()) { <p class="mx-auto mt-4 max-w-2xl text-lg text-neutral-600 dark:text-neutral-400">{{ subtitle() }}</p> }
        </div>

        @for (g of groups(); track $index) {
          <div class="mt-14">
            <h3 class="font-[family-name:var(--site-head-font)] text-xl font-bold" style="color:var(--site-primary)">{{ g.category }}</h3>
            <div class="mt-5 flex flex-col divide-y divide-neutral-100 dark:divide-neutral-800">
              @for (it of g.items; track $index) {
                <div class="flex items-baseline gap-4 py-3">
                  <div class="min-w-0 flex-1">
                    <p class="font-semibold text-neutral-900 dark:text-white">{{ it.name }}</p>
                    @if (it.description) { <p class="mt-0.5 text-sm text-neutral-500">{{ it.description }}</p> }
                  </div>
                  <!-- Noktalı dolgu: klasik menü dizgisi, fiyatı sağa yaslar. -->
                  <span class="hidden flex-1 border-b border-dotted border-neutral-300 dark:border-neutral-700 sm:block"></span>
                  @if (it.price) { <span class="shrink-0 font-bold" style="color:var(--site-primary)">{{ it.price }}</span> }
                </div>
              }
            </div>
          </div>
        }
      </div>
    </section>
  `,
})
export class MenuDisplaySection {
  protected text = inject(SiteChromeText);
  config = input<SiteConfig>({});
  protected eyebrow = computed(() => cfg(this.config(), 'eyebrow', this.text.t('fb.menu')));
  protected title = computed(() => cfg(this.config(), 'title', 'Lezzet Listemiz'));
  protected subtitle = computed(() => cfg(this.config(), 'subtitle', ''));
  protected groups = computed<{ category: string; items: { name: string; description?: string; price?: string }[] }[]>(() => {
    const raw = this.config()['groups'];
    return Array.isArray(raw) && raw.length ? raw.filter(g => g?.category) : [
      { category: this.text.t('fb.baslangiclar'), items: [
        { name: this.text.t('fb.mevsimSalatasi'), description: this.text.t('fb.tazeYesilliklerCevizNar'), price: '₺180' },
        { name: this.text.t('fb.mercimekCorbasi'), description: this.text.t('fb.evYapimiTereyagli'), price: '₺140' },
      ] },
      { category: 'Ana Yemekler', items: [
        { name: 'Izgara Levrek', description: this.text.t('fb.sebzeGarniturVeLimon'), price: '₺520' },
        { name: this.text.t('fb.kuzuSis'), description: this.text.t('fb.kozlenmisBiberVePilav'), price: '₺480' },
      ] },
      { category: this.text.t('fb.tatlilar'), items: [
        { name: this.text.t('fb.firinSutlac'), description: this.text.t('fb.antepFistikli'), price: '₺160' },
      ] },
    ];
  });
}

// ── Masa Rezervasyonu (restoran) ────────────────────────────────────────────
@Component({
  selector: 'sec-table-reservation',
  standalone: true,
  imports: [MIcon],
  template: `
    <section id="reservation" class="py-24" style="background-color:var(--site-primary)">
      <div class="mx-auto max-w-3xl px-6 text-center lg:px-8">
        <h2 class="font-[family-name:var(--site-head-font)] text-3xl font-bold tracking-tight text-white sm:text-4xl">{{ title() }}</h2>
        @if (subtitle()) { <p class="mx-auto mt-4 max-w-xl text-lg text-white/80">{{ subtitle() }}</p> }

        <div class="mt-10 grid gap-4 sm:grid-cols-3">
          @for (n of notes(); track $index) {
            <div class="rounded-2xl bg-white/10 p-5 text-left">
              <m-icon [name]="n.icon" class="text-white/90" />
              <p class="mt-2 text-sm font-semibold text-white">{{ n.label }}</p>
              <p class="mt-0.5 text-sm text-white/70">{{ n.value }}</p>
            </div>
          }
        </div>

        <!-- Rezervasyon akışı Randevu modülünde; bölüm oraya yönlendirir.
             Kendi form alanlarını çizmek iki ayrı rezervasyon defteri
             oluştururdu. -->
        <a [href]="ctaUrl()" class="ws-btn mt-10 inline-flex items-center gap-2 rounded-full bg-white px-8 py-3 font-semibold shadow-lg transition hover:shadow-xl" style="color:var(--site-primary)">
          <m-icon name="calendar-check" size="sm" />{{ ctaText() }}
        </a>
      </div>
    </section>
  `,
})
export class TableReservationSection {
  protected text = inject(SiteChromeText);
  config = input<SiteConfig>({});
  protected title = computed(() => cfg(this.config(), 'title', this.text.t('fb.masaniziAyirtin')));
  protected subtitle = computed(() => cfg(this.config(), 'subtitle', this.text.t('fb.haftaSonuIcinErken')));
  protected ctaText = computed(() => cfg(this.config(), 'ctaText', 'Rezervasyon Yap'));
  protected ctaUrl = computed(() => cfg(this.config(), 'ctaUrl', '#contact'));
  protected notes = computed<{ icon: string; label: string; value: string }[]>(() => {
    const raw = this.config()['notes'];
    return Array.isArray(raw) && raw.length ? raw.filter(n => n?.label) : [
      { icon: 'clock', label: this.text.t('fb.calismaSaatleri'), value: this.text.t('fb.herGun1200') },
      { icon: 'users', label: 'Grup Rezervasyonu', value: this.text.t('fb.8KisiVeUzeri') },
      { icon: 'phone', label: 'Telefon', value: '+1 647 000 0000' },
    ];
  });
}

// ── Oda Izgarası (otel / konaklama) ─────────────────────────────────────────
@Component({
  selector: 'sec-room-grid',
  standalone: true,
  imports: [MIcon],
  template: `
    <section id="rooms" class="bg-neutral-50 py-24 dark:bg-neutral-900">
      <div class="mx-auto max-w-7xl px-6 lg:px-8">
        <div class="text-center">
          @if (eyebrow()) { <p class="text-sm font-semibold uppercase tracking-widest" style="color:var(--site-accent)">{{ eyebrow() }}</p> }
          <h2 class="mt-3 font-[family-name:var(--site-head-font)] text-3xl font-bold tracking-tight text-neutral-900 dark:text-white sm:text-4xl">{{ title() }}</h2>
          @if (subtitle()) { <p class="mx-auto mt-4 max-w-2xl text-lg text-neutral-600 dark:text-neutral-400">{{ subtitle() }}</p> }
        </div>

        <div class="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          @for (r of rooms(); track $index) {
            <div class="flex flex-col overflow-hidden rounded-2xl border border-neutral-200 bg-white dark:border-neutral-800 dark:bg-neutral-950">
              <div class="aspect-[4/3] bg-neutral-100 dark:bg-neutral-800">
                @if (r.image) { <img [src]="r.image" [alt]="r.name" class="h-full w-full object-cover" /> }
              </div>
              <div class="flex flex-1 flex-col p-5">
                <h3 class="text-lg font-bold text-neutral-900 dark:text-white">{{ r.name }}</h3>
                @if (r.description) { <p class="mt-1 text-sm text-neutral-600 dark:text-neutral-400">{{ r.description }}</p> }
                @if (r.features?.length) {
                  <ul class="mt-3 flex flex-wrap gap-2">
                    @for (f of r.features; track $index) {
                      <li class="rounded-full bg-neutral-100 px-2.5 py-1 text-xs text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300">{{ f }}</li>
                    }
                  </ul>
                }
                <div class="mt-4 flex items-end justify-between pt-2">
                  @if (r.price) {
                    <span class="text-xl font-bold" style="color:var(--site-primary)">{{ r.price }}
                      <span class="text-sm font-normal text-neutral-500">{{ perNight() }}</span>
                    </span>
                  }
                  @if (r.capacity) {
                    <span class="flex items-center gap-1 text-sm text-neutral-500"><m-icon name="users" size="sm" />{{ r.capacity }}</span>
                  }
                </div>
              </div>
            </div>
          }
        </div>
      </div>
    </section>
  `,
})
export class RoomGridSection {
  protected text = inject(SiteChromeText);
  config = input<SiteConfig>({});
  protected eyebrow = computed(() => cfg(this.config(), 'eyebrow', this.text.t('fb.odalarimiz')));
  protected title = computed(() => cfg(this.config(), 'title', this.text.t('fb.konaklamaSecenekleri')));
  protected subtitle = computed(() => cfg(this.config(), 'subtitle', ''));
  private chrome = inject(SiteChromeText);

  // Fiyatın yanındaki birim arayüz metni — sitenin dilinden gelir.
  protected perNight = computed(() =>
    cfg(this.config(), 'perNightText', '') || this.chrome.t('rm.perNight'));
  protected rooms = computed<{ image?: string; name: string; description?: string; features?: string[]; price?: string; capacity?: string }[]>(() => {
    const raw = this.config()['rooms'];
    return Array.isArray(raw) && raw.length ? raw.filter(r => r?.name) : [
      { name: 'Standart Oda', description: this.text.t('fb.sehirManzaraliCiftKisilik'), features: [this.text.t('fb.ucretsizWiFi'), 'Klima', 'Mini bar'], price: '₺2.400', capacity: this.text.t('fb.2Kisi') },
      { name: 'Deluxe Oda', description: this.text.t('fb.genisBalkonVeOturma'), features: ['Balkon', this.text.t('fb.calismaMasasi'), 'Kahve makinesi'], price: '₺3.600', capacity: this.text.t('fb.23Kisi') },
      { name: 'Aile Suiti', description: this.text.t('fb.ikiYatakOdasiSalon'), features: [this.text.t('fb.2YatakOdasi'), 'Mutfak', this.text.t('fb.ucretsizOtopark')], price: '₺5.200', capacity: this.text.t('fb.4Kisi') },
    ];
  });
}

// ── İlan Arama (emlak) ──────────────────────────────────────────────────────
@Component({
  selector: 'sec-listing-search',
  standalone: true,
  imports: [MIcon],
  template: `
    <section id="listings" class="bg-white py-24 dark:bg-neutral-950">
      <div class="mx-auto max-w-6xl px-6 lg:px-8">
        <div class="text-center">
          @if (eyebrow()) { <p class="text-sm font-semibold uppercase tracking-widest" style="color:var(--site-accent)">{{ eyebrow() }}</p> }
          <h2 class="mt-3 font-[family-name:var(--site-head-font)] text-3xl font-bold tracking-tight text-neutral-900 dark:text-white sm:text-4xl">{{ title() }}</h2>
          @if (subtitle()) { <p class="mx-auto mt-4 max-w-2xl text-lg text-neutral-600 dark:text-neutral-400">{{ subtitle() }}</p> }
        </div>

        <!-- Arama İSTEĞİ üretmez: portföy sorgusu portal tarafında.
             Buradaki alanlar müşteriyi ilan sayfasına taşıyan bir vitrindir;
             gerçek bir sonuç listesi vaadi vermemek için buton tek çıkış. -->
        <div class="mt-10 rounded-2xl border border-neutral-200 bg-neutral-50 p-6 dark:border-neutral-800 dark:bg-neutral-900">
          <div class="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            @for (f of filters(); track $index) {
              <div>
                <p class="mb-1.5 text-xs font-semibold uppercase tracking-wide text-neutral-500">{{ f.label }}</p>
                <div class="flex items-center justify-between rounded-lg border border-neutral-200 bg-white px-3 py-2.5 text-sm text-neutral-500 dark:border-neutral-700 dark:bg-neutral-950">
                  {{ f.placeholder }}<m-icon name="chevron-down" size="sm" class="text-neutral-400" />
                </div>
              </div>
            }
          </div>
          <a [href]="ctaUrl()" class="ws-btn mt-5 inline-flex w-full items-center justify-center gap-2 rounded-lg px-6 py-3 font-semibold text-white transition hover:opacity-90 sm:w-auto" style="background-color:var(--site-primary)">
            <m-icon name="search" size="sm" />{{ ctaText() }}
          </a>
        </div>
      </div>
    </section>
  `,
})
export class ListingSearchSection {
  protected text = inject(SiteChromeText);
  config = input<SiteConfig>({});
  protected eyebrow = computed(() => cfg(this.config(), 'eyebrow', this.text.t('fb.portfoy')));
  protected title = computed(() => cfg(this.config(), 'title', 'Hayalinizdeki Yeri Bulun'));
  protected subtitle = computed(() => cfg(this.config(), 'subtitle', ''));
  protected ctaText = computed(() => cfg(this.config(), 'ctaText', this.text.t('fb.ilanlariGoruntule')));
  protected ctaUrl = computed(() => cfg(this.config(), 'ctaUrl', '#contact'));
  protected filters = computed<{ label: string; placeholder: string }[]>(() => {
    const raw = this.config()['filters'];
    return Array.isArray(raw) && raw.length ? raw.filter(f => f?.label) : [
      { label: this.text.t('fb.islem'), placeholder: this.text.t('fb.satilikKiralik') },
      { label: 'Tip', placeholder: 'Daire, villa, arsa' },
      { label: 'Konum', placeholder: this.text.t('fb.ilceVeyaSemt') },
      { label: this.text.t('fb.butce'), placeholder: this.text.t('fb.fiyatAraligi') },
    ];
  });
}

// ── Ders Programı (fitness / eğitim / kulüp) ────────────────────────────────
@Component({
  selector: 'sec-class-schedule',
  standalone: true,
  template: `
    <section id="schedule" class="bg-neutral-50 py-24 dark:bg-neutral-900">
      <div class="mx-auto max-w-6xl px-6 lg:px-8">
        <div class="text-center">
          @if (eyebrow()) { <p class="text-sm font-semibold uppercase tracking-widest" style="color:var(--site-accent)">{{ eyebrow() }}</p> }
          <h2 class="mt-3 font-[family-name:var(--site-head-font)] text-3xl font-bold tracking-tight text-neutral-900 dark:text-white sm:text-4xl">{{ title() }}</h2>
          @if (subtitle()) { <p class="mx-auto mt-4 max-w-2xl text-lg text-neutral-600 dark:text-neutral-400">{{ subtitle() }}</p> }
        </div>

        <!-- Dar ekranda tablo kırılmasın: kendi kabında yatay kayar. -->
        <div class="mt-12 overflow-x-auto rounded-2xl border border-neutral-200 bg-white dark:border-neutral-800 dark:bg-neutral-950">
          <table class="w-full min-w-[640px] text-left text-sm">
            <thead>
              <tr class="border-b border-neutral-200 dark:border-neutral-800">
                <th class="px-5 py-3 font-semibold text-neutral-500">{{ timeLabel() }}</th>
                @for (d of days(); track $index) { <th class="px-5 py-3 font-semibold text-neutral-900 dark:text-white">{{ d }}</th> }
              </tr>
            </thead>
            <tbody>
              @for (r of rows(); track $index) {
                <tr class="border-b border-neutral-100 last:border-0 dark:border-neutral-800">
                  <td class="whitespace-nowrap px-5 py-3 font-semibold" style="color:var(--site-primary)">{{ r.time }}</td>
                  @for (c of r.cells; track $index) {
                    <td class="px-5 py-3 text-neutral-600 dark:text-neutral-400">{{ c || '—' }}</td>
                  }
                </tr>
              }
            </tbody>
          </table>
        </div>
      </div>
    </section>
  `,
})
export class ClassScheduleSection {
  protected text = inject(SiteChromeText);
  config = input<SiteConfig>({});
  protected eyebrow = computed(() => cfg(this.config(), 'eyebrow', 'Program'));
  protected title = computed(() => cfg(this.config(), 'title', this.text.t('fb.haftalikDersProgrami')));
  protected subtitle = computed(() => cfg(this.config(), 'subtitle', ''));
  protected timeLabel = computed(() => cfg(this.config(), 'timeLabel', 'Saat'));
  protected days = computed<string[]>(() => {
    const raw = this.config()['days'];
    return Array.isArray(raw) && raw.length ? raw : ['Pzt', 'Sal', this.text.t('fb.car'), 'Per', 'Cum', 'Cmt'];
  });
  protected rows = computed<{ time: string; cells: string[] }[]>(() => {
    const raw = this.config()['rows'];
    return Array.isArray(raw) && raw.length ? raw.filter(r => r?.time) : [
      { time: '07:00', cells: ['Sabah HIIT', '', 'Sabah HIIT', '', 'Sabah HIIT', ''] },
      { time: '09:30', cells: ['Pilates', 'Yoga', 'Pilates', 'Yoga', 'Pilates', 'Yoga'] },
      { time: '18:00', cells: ['Fonksiyonel', 'Spinning', 'Fonksiyonel', 'Spinning', 'Fonksiyonel', 'Serbest'] },
      { time: '20:00', cells: ['Kickbox', 'Esneme', 'Kickbox', 'Esneme', '', ''] },
    ];
  });
}

// ── Üyelik Planları (fitness / kulüp) ───────────────────────────────────────
@Component({
  selector: 'sec-membership-plans',
  standalone: true,
  imports: [MIcon],
  template: `
    <section id="plans" class="bg-white py-24 dark:bg-neutral-950">
      <div class="mx-auto max-w-6xl px-6 lg:px-8">
        <div class="text-center">
          @if (eyebrow()) { <p class="text-sm font-semibold uppercase tracking-widest" style="color:var(--site-accent)">{{ eyebrow() }}</p> }
          <h2 class="mt-3 font-[family-name:var(--site-head-font)] text-3xl font-bold tracking-tight text-neutral-900 dark:text-white sm:text-4xl">{{ title() }}</h2>
          @if (subtitle()) { <p class="mx-auto mt-4 max-w-2xl text-lg text-neutral-600 dark:text-neutral-400">{{ subtitle() }}</p> }
        </div>

        <div class="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          @for (p of plans(); track $index) {
            <div class="flex flex-col rounded-2xl border-2 p-6"
                 [class]="p.highlighted ? 'shadow-xl' : 'border-neutral-200 dark:border-neutral-800'"
                 [style.border-color]="p.highlighted ? 'var(--site-primary)' : null">
              @if (p.highlighted && badge()) {
                <span class="mb-3 self-start rounded-full px-3 py-1 text-xs font-bold text-white" style="background-color:var(--site-primary)">{{ badge() }}</span>
              }
              <h3 class="text-lg font-bold text-neutral-900 dark:text-white">{{ p.name }}</h3>
              <p class="mt-3 text-3xl font-bold" style="color:var(--site-primary)">{{ p.price }}
                @if (p.period) { <span class="text-base font-normal text-neutral-500">{{ p.period }}</span> }
              </p>
              @if (p.features?.length) {
                <ul class="mt-5 flex flex-1 flex-col gap-2.5">
                  @for (f of p.features; track $index) {
                    <li class="flex items-start gap-2 text-sm text-neutral-600 dark:text-neutral-400">
                      <m-icon name="check" size="sm" class="mt-0.5 shrink-0" [style.color]="'var(--site-primary)'" />{{ f }}
                    </li>
                  }
                </ul>
              }
              <a [href]="p.ctaUrl || '#contact'" class="ws-btn mt-6 rounded-lg px-5 py-2.5 text-center font-semibold transition"
                 [class]="p.highlighted ? 'text-white hover:opacity-90' : 'border border-neutral-300 text-neutral-700 hover:bg-neutral-50 dark:border-neutral-700 dark:text-neutral-200'"
                 [style.background-color]="p.highlighted ? 'var(--site-primary)' : null">
                {{ p.ctaText || 'Üye Ol' }}
              </a>
            </div>
          }
        </div>
      </div>
    </section>
  `,
})
export class MembershipPlansSection {
  protected text = inject(SiteChromeText);
  config = input<SiteConfig>({});
  protected eyebrow = computed(() => cfg(this.config(), 'eyebrow', this.text.t('fb.uyelik')));
  protected title = computed(() => cfg(this.config(), 'title', this.text.t('fb.sizeUygunPaketiSecin')));
  protected subtitle = computed(() => cfg(this.config(), 'subtitle', ''));
  protected badge = computed(() => cfg(this.config(), 'badgeText', this.text.t('fb.enPopuler')));
  protected plans = computed<{ name: string; price: string; period?: string; features?: string[]; highlighted?: boolean; ctaText?: string; ctaUrl?: string }[]>(() => {
    const raw = this.config()['plans'];
    return Array.isArray(raw) && raw.length ? raw.filter(p => p?.name) : [
      { name: this.text.t('fb.aylik'), price: '₺1.200', period: '/ ay', features: [this.text.t('fb.sinirsizSalonKullanimi'), 'Grup dersleri', this.text.t('fb.soyunmaDolabi')] },
      { name: this.text.t('fb.6Aylik'), price: '₺5.400', period: '/ 6 ay', features: [this.text.t('fb.aylikPaketinTumu'), this.text.t('fb.1KisiselAntrenman'), this.text.t('fb.vucutAnalizi')], highlighted: true },
      { name: this.text.t('fb.yillik'), price: '₺9.600', period: '/ yıl', features: [this.text.t('fb.6AylikPaketinTumu'), this.text.t('fb.4KisiselAntrenman'), this.text.t('fb.beslenmeDanismanligi')] },
    ];
  });
}

// ── Personel Kartları (güzellik / klinik / hizmet) ──────────────────────────
@Component({
  selector: 'sec-staff-grid',
  standalone: true,
  imports: [MIcon],
  template: `
    <section id="staff" class="bg-neutral-50 py-24 dark:bg-neutral-900">
      <div class="mx-auto max-w-7xl px-6 lg:px-8">
        <div class="text-center">
          @if (eyebrow()) { <p class="text-sm font-semibold uppercase tracking-widest" style="color:var(--site-accent)">{{ eyebrow() }}</p> }
          <h2 class="mt-3 font-[family-name:var(--site-head-font)] text-3xl font-bold tracking-tight text-neutral-900 dark:text-white sm:text-4xl">{{ title() }}</h2>
          @if (subtitle()) { <p class="mx-auto mt-4 max-w-2xl text-lg text-neutral-600 dark:text-neutral-400">{{ subtitle() }}</p> }
        </div>

        <div class="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          @for (s of staff(); track $index) {
            <div class="rounded-2xl border border-neutral-200 bg-white p-6 text-center dark:border-neutral-800 dark:bg-neutral-950">
              <div class="mx-auto size-24 overflow-hidden rounded-full bg-neutral-100 dark:bg-neutral-800">
                @if (s.photo) { <img [src]="s.photo" [alt]="s.name" class="h-full w-full object-cover" /> }
              </div>
              <h3 class="mt-4 font-bold text-neutral-900 dark:text-white">{{ s.name }}</h3>
              @if (s.role) { <p class="text-sm" style="color:var(--site-primary)">{{ s.role }}</p> }
              @if (s.bio) { <p class="mt-2 text-sm leading-relaxed text-neutral-600 dark:text-neutral-400">{{ s.bio }}</p> }
              @if (s.specialties?.length) {
                <ul class="mt-3 flex flex-wrap justify-center gap-1.5">
                  @for (sp of s.specialties; track $index) {
                    <li class="rounded-full bg-neutral-100 px-2.5 py-0.5 text-xs text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300">{{ sp }}</li>
                  }
                </ul>
              }
              @if (s.bookUrl) {
                <a [href]="s.bookUrl" class="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold" style="color:var(--site-primary)">
                  <m-icon name="calendar-days" size="sm" />{{ bookText() }}
                </a>
              }
            </div>
          }
        </div>
      </div>
    </section>
  `,
})
export class StaffGridSection {
  protected text = inject(SiteChromeText);
  config = input<SiteConfig>({});
  protected eyebrow = computed(() => cfg(this.config(), 'eyebrow', 'Ekibimiz'));
  protected title = computed(() => cfg(this.config(), 'title', 'Uzman Kadromuz'));
  protected subtitle = computed(() => cfg(this.config(), 'subtitle', ''));
  protected bookText = computed(() => cfg(this.config(), 'bookText', 'Randevu al'));
  protected staff = computed<{ photo?: string; name: string; role?: string; bio?: string; specialties?: string[]; bookUrl?: string }[]>(() => {
    const raw = this.config()['staff'];
    return Array.isArray(raw) && raw.length ? raw.filter(s => s?.name) : [
      { name: 'Uzman 1', role: this.text.t('fb.kidemliUzman'), specialties: [this.text.t('fb.uzmanlikA'), this.text.t('fb.uzmanlikB')] },
      { name: 'Uzman 2', role: 'Uzman', specialties: [this.text.t('fb.uzmanlikC')] },
      { name: 'Uzman 3', role: 'Uzman', specialties: [this.text.t('fb.uzmanlikD')] },
      { name: 'Uzman 4', role: this.text.t('fb.danisman'), specialties: [this.text.t('fb.uzmanlikE')] },
    ];
  });
}

// ── Randevu Kutusu (klinik / güzellik / danışman) ───────────────────────────
@Component({
  selector: 'sec-appointment-widget',
  standalone: true,
  imports: [MIcon],
  template: `
    <section id="appointment" class="py-24" style="background-color:var(--site-primary)">
      <div class="mx-auto max-w-4xl px-6 lg:px-8">
        <div class="grid items-center gap-10 md:grid-cols-2">
          <div>
            @if (eyebrow()) { <p class="text-sm font-semibold uppercase tracking-widest text-white/70">{{ eyebrow() }}</p> }
            <h2 class="mt-3 font-[family-name:var(--site-head-font)] text-3xl font-bold tracking-tight text-white sm:text-4xl">{{ title() }}</h2>
            @if (subtitle()) { <p class="mt-4 text-lg text-white/80">{{ subtitle() }}</p> }
            <a [href]="ctaUrl()" class="ws-btn mt-8 inline-flex items-center gap-2 rounded-full bg-white px-8 py-3 font-semibold shadow-lg transition hover:shadow-xl" style="color:var(--site-primary)">
              <m-icon name="calendar-check" size="sm" />{{ ctaText() }}
            </a>
          </div>
          <div class="flex flex-col gap-3">
            @for (s of steps(); track $index) {
              <div class="flex items-start gap-3 rounded-xl bg-white/10 p-4">
                <span class="flex size-7 shrink-0 items-center justify-center rounded-full bg-white text-sm font-bold" style="color:var(--site-primary)">{{ $index + 1 }}</span>
                <div>
                  <p class="font-semibold text-white">{{ s.title }}</p>
                  @if (s.description) { <p class="mt-0.5 text-sm text-white/70">{{ s.description }}</p> }
                </div>
              </div>
            }
          </div>
        </div>
      </div>
    </section>
  `,
})
export class AppointmentWidgetSection {
  protected text = inject(SiteChromeText);
  config = input<SiteConfig>({});
  protected eyebrow = computed(() => cfg(this.config(), 'eyebrow', 'Randevu'));
  protected title = computed(() => cfg(this.config(), 'title', this.text.t('fb.randevunuzuHemenAlin')));
  protected subtitle = computed(() => cfg(this.config(), 'subtitle', this.text.t('fb.uygunSaatiSecinGerisini')));
  protected ctaText = computed(() => cfg(this.config(), 'ctaText', 'Randevu Al'));
  protected ctaUrl = computed(() => cfg(this.config(), 'ctaUrl', '#contact'));
  protected steps = computed<{ title: string; description?: string }[]>(() => {
    const raw = this.config()['steps'];
    return Array.isArray(raw) && raw.length ? raw.filter(s => s?.title) : [
      { title: this.text.t('fb.hizmetiSecin'), description: this.text.t('fb.ihtiyacinizaUygunHizmetiBelirleyin') },
      { title: 'Tarih ve saat', description: this.text.t('fb.sizeUygunBosSaati') },
      { title: 'Onay', description: this.text.t('fb.randevunuzEPostaIle') },
    ];
  });
}
